import api from '../../../services/api';

/**
 * Fetch all PPT version history for a given RFP.
 * @param {string} rfpId
 * @returns {Promise<Array>}
 */
export const getPptVersions = async (rfpId) => {
    const response = await api.get(`/ppt-config/rfp/${rfpId}/ppt/versions`);
    return response.data; // Expected: array of version objects
};

/**
 * Create a new PPT checkpoint (version).
 * @param {string} rfpId
 * @param {string} comment - Optional comment
 * @returns {Promise<Object>}
 */
export const createPptVersion = async (rfpId, comment) => {
    const payload = comment ? { comment } : {};
    const response = await api.post(`/ppt-config/rfp/${rfpId}/ppt/versions`, payload);
    return response.data; // Expected: { status, message, version_number }
};

/**
 * Revert PPT config to a specific version.
 * @param {string} rfpId
 * @param {number} versionNumber
 * @returns {Promise<Object>}
 */
export const restorePptVersion = async (rfpId, versionNumber) => {
    const response = await api.post(`/ppt-config/rfp/${rfpId}/ppt/versions/${versionNumber}/revert`);
    return response.data; // Expected: { status, message, new_version_number }
};

/**
 * Compare two PPT versions.
 * @param {string} rfpId
 * @param {number} versionA
 * @param {number} versionB
 * @returns {Promise<Object>}
 */
export const comparePptVersions = async (rfpId, versionA, versionB) => {
    const response = await api.get(`/ppt-config/rfp/${rfpId}/ppt/versions/compare`, {
        params: { v1: parseInt(versionA, 10), v2: parseInt(versionB, 10) }
    });
    return response.data;
};

/**
 * Delete a specific PPT version.
 * DELETE /ppt-config/rfp/{rfp_id}/ppt/versions/{version_number}
 * Headers: rfp_id, version_number
 * @param {string} rfpId
 * @param {number} versionNumber
 * @returns {Promise<Object>}
 */
export const deletePptVersion = async (rfpId, versionNumber) => {
    const response = await api.delete(`/ppt-config/rfp/${rfpId}/ppt/versions/${versionNumber}`, {
        headers: {
            'rfp_id': rfpId,
            'version_number': String(versionNumber)
        }
    });
    return response.data;
};

/**
 * Download a specific PPT version report.
 * GET /rfp/report/{rfp_id}?format=pptx&language={lang}&version={vNum}&name={name}&comment={comment}
 * @param {string} rfpId
 * @param {Object} version
 * @param {string} language
 */
export const downloadPptVersionReport = async (rfpId, version, language = 'en') => {
    const vNum = version.version_number || version;
    const vName = version.name || version.title || `Version_${vNum}`;
    const vComment = version.comment || `PPT Report Version ${vNum}`;
    const lang = language.toLowerCase();

    const response = await api.get(`/rfp/report/${rfpId}`, {
        params: {
            format: 'pptx',
            language: lang,
            version: vNum,
            name: vName,
            comment: vComment
        },
        headers: {
            'Accept-Language': lang,
            'language': lang
        },
        responseType: 'blob'
    });

    const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.presentationml.presentation'
    });
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = `${vName.replace(/[^a-zA-Z0-9_-]/g, '_')}_v${vNum}.pptx`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(downloadUrl);
};
